import { cn } from '../lib/utils';
import { X } from 'lucide-react';

interface GroupBadgeProps {
  /** Numeric group ID */
  groupId: string | number;
  /** Human-readable name from group map */
  groupName: string;
  /** default: "Name (ID)", compact: ID only with name on hover */
  variant?: 'default' | 'compact';
  /** Optional callback; if provided, renders an X icon button for removal */
  onRemove?: () => void;
}

/**
 * GroupBadge — displays a forum group as a gold-accented chip.
 *
 * Follows the GroupBadge visual contract (UI-SPEC.md §1):
 * - Default: "Name (ID)" text, gold border, 10% gold background
 * - Compact: ID only, full name on native tooltip via title attribute
 * - Removable: X icon appended, hover:text-white on hover
 */
export function GroupBadge({ groupId, groupName, variant = 'default', onRemove }: GroupBadgeProps) {
  const displayText = variant === 'compact'
    ? String(groupId)
    : `${groupName} (${groupId})`;

  const tooltipTitle = variant === 'compact'
    ? `${groupName} (${groupId})`
    : undefined;

  return (
    <span className={cn('eo-badge--group')}>
      <span title={tooltipTitle}>{displayText}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-1.5 hover:text-white"
          aria-label={`Remove ${groupName}`}
        >
          <X size={12} />
        </button>
      )}
    </span>
  );
}
